import React, { useEffect, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'

import {
  Student,
  Address,
  Responsible,
  Intent,
  Quiz,
  Confirmation,
  Finished,
} from './Steps'

import {
  constants,
  pendingSubmissionStorage,
  studentCpfStorage,
  subscription as subscriptionUtils,
} from '../../../utils'
import { PageHeader } from '../../../components/Layout'
import { Progress } from '../../../components'
import SubscriptionStatusModal from '../../../components/SubscriptionStatusModal'
import { appService, subscriptionService } from '../../../services'

const { enrollmentToStudent, newRegistrationToStudent } = subscriptionUtils

const getInitialData = () => {
  const { initialData } = window

  if (initialData) {
    try {
      return JSON.parse(initialData)
    } catch (e) {
      console.error(e)
    }
  }

  return {
    student: {},
    address: {},
    responsible: { newsletter: true },
    intent: {
      optOne: {},
      optTwo: {},
      optThree: {},
    },
    quiz: {},
  }
}

const handledStudent = state => {
  const data = getInitialData()
  const { student, uuid } = data

  if (student?.cpf) {
    return { ...student, disableCpf: !!uuid }
  }

  if (state?.newRegistration) {
    return {
      ...student,
      ...newRegistrationToStudent(state.studentCpf, state.birthdate),
    }
  }

  if (state?.fromReenrollment && state?.reenrollment) {
    return {
      ...student,
      ...enrollmentToStudent(
        state.reenrollment,
        state.studentCpf,
        state.birthdate
      ),
    }
  }

  const studentCpf = state?.studentCpf || studentCpfStorage.getStudentCpf()

  if (studentCpf) {
    return { ...student, cpf: studentCpf, disableCpf: true }
  }

  return { ...student }
}

const Create = () => {
  const history = useHistory()
  const location = useLocation()

  const [confirmation, setConfirmation] = useState({})
  const [count, setCount] = useState(0)
  const [step, setStep] = useState(0)
  const [errors, setErrors] = useState({})
  const [validating, setValidating] = useState(false)
  const [pendingJid, setPendingJid] = useState(
    pendingSubmissionStorage.getJid()
  )

  const [subscription, setSubscription] = useState({
    ...getInitialData(),
    student: handledStudent(location.state),
  })

  const finishPendingSubmission = () => {
    pendingSubmissionStorage.clearJid()
    setPendingJid('')
  }

  const reviewAfterPendingFailure = nextStep => {
    finishPendingSubmission()
    setStep(nextStep)
  }

  const next = () => setStep(step + 1)
  const prev = () => setStep(step - 1)

  const persistSubscription = attr => () => {
    setSubscription({ ...subscription, ...attr })
  }

  const tryToValidate = async () => {
    setValidating(true)

    const { success, payload } = await subscriptionService.sendToValidate(
      subscription,
      step
    )

    setErrors(payload.errors)
    setValidating(false)

    if (success) {
      next()
    }
  }

  const retrieveSubscriptionData = async () => {
    const { success, payload } = await subscriptionService.getSubscriptionAndEnrollment(
      subscription.student.cpf,
      subscription.student.birthdate
    )

    if (success) {
      setConfirmation(JSON.parse(payload.subscription).data.attributes)
    } else {
      setErrors(payload.errors)
    }
  }

  useEffect(() => {
    if (appService.shouldRedirectToHome('subscribe')) {
      history.push(constants.routes.home.path)
    }
  }, [])

  useEffect(() => {
    if (subscription.uuid && history.location.pathname === '/nova') {
      history.push(constants.routes.subscriptions.edit.path)
    } else if (subscription.uuid === undefined && history.location.pathname === '/editar') {
      history.push(constants.routes.subscriptions.create.path)
    }
  }, [subscription])

  /*
    workaround por conta de não ter um callback para garantir o update e o
    useEffect executar logo no primeiro render
  */

  useEffect(() => {
    if (count > 0 && step !== 5) {
      tryToValidate()
    } else {
      setCount(count + 1)
    }
  }, [subscription])

  useEffect(() => {
    if (step === 5) {
      next()
    }
  }, [confirmation])

  const steps = [
    'Dados do candidato',
    'Endereço',
    'Dados do responsável',
    'Escolas e quadros curriculares',
    'Questionário',
    'Confirmação dos dados',
    'Inscrição realizada',
  ]

  const renderStep = () => {
    switch (step) {
      case 0:
        return (
          <Student
            data={subscription.student}
            errors={errors}
            persist={persistSubscription}
            validating={validating}
          />
        )
      case 1:
        return (
          <Address
            data={subscription.address}
            errors={errors}
            persist={persistSubscription}
            prev={prev}
            validating={validating}
          />
        )
      case 2:
        return (
          <Responsible
            data={subscription}
            errors={errors}
            persist={persistSubscription}
            prev={prev}
            validating={validating}
          />
        )
      case 3:
        return (
          <Intent
            data={subscription}
            errors={errors}
            persist={persistSubscription}
            prev={prev}
            validating={validating}
          />
        )
      case 4:
        return (
          <Quiz
            data={subscription.quiz}
            errors={errors}
            persist={persistSubscription}
            prev={prev}
            validating={validating}
          />
        )
      case 5:
        return (
          <Confirmation
            data={subscription}
            prev={prev}
            retrieveSubscriptionData={retrieveSubscriptionData}
            setStep={setStep}
          />
        )
      default:
        return <Finished data={confirmation} student={subscription.student} />
    }
  }

  const shouldDisplayProgress = () => step + 1 < steps.length

  const lastStep = () => step + 1 >= steps.length

  const backButtonTitle = () => {
    if (lastStep()) {
      return null
    }

    return subscription.uuid ? 'Cancelar atualização' : 'Cancelar'
  }

  return (
    <>
      <PageHeader
        buttonText={backButtonTitle()}
        disabled={validating}
        title={steps[step]}
        onClick={
          lastStep()
            ? null
            : () => {
                studentCpfStorage.clearStudentCpf()
                history.push(constants.routes.home.path)
              }
        }
      />

      {shouldDisplayProgress() && (
        <Progress steps={steps.length} value={step} />
      )}

      {renderStep()}

      {pendingJid && (
        <SubscriptionStatusModal
          jid={pendingJid}
          open
          setStep={reviewAfterPendingFailure}
          retrieveSubscriptionData={() => {}}
          onClose={finishPendingSubmission}
          onSuccess={() => {
            finishPendingSubmission()
            history.push(constants.routes.subscriptions.search.path)
          }}
          onError={finishPendingSubmission}
          onFailed={pendingSubmissionStorage.clearJid}
          onRenewEnrollmentRequested={finishPendingSubmission}
        />
      )}
    </>
  )
}

export default Create
